/**
 * Flushing telemetry when the page goes away.
 *
 * The queue ships every `flushIntervalMs` (10 s by default), so without this a
 * visitor who closes the tab mid-interval takes up to ten seconds of events with
 * them. On short sessions — a bounce, a back-button — that is most of the
 * session's telemetry, and the loss is invisible: the events were never sent, so
 * nothing reports them missing.
 *
 * The transport already sets `keepalive: true` on the event batch, which is what
 * lets a request outlive the document. What was missing is anything to TRIGGER
 * the flush.
 *
 * **`visibilitychange` → hidden is the primary signal, not `beforeunload`.**
 * `beforeunload` is unreliable on mobile, where a tab is more often backgrounded
 * and killed than closed, and registering it disqualifies the page from the
 * back/forward cache — a real performance cost imposed on the publisher for our
 * telemetry. `pagehide` is added as a backstop for the browsers where the
 * visibility transition is skipped.
 */

/** Wire flush-on-hide. Returns a teardown so a destroyed SDK leaves nothing
 *  behind — a listener that outlives its client keeps the whole client alive. */
export function attachFlushOnHide(flush, target = typeof document !== "undefined" ? document : undefined, win = typeof window !== "undefined" ? window : undefined) {
  if (typeof flush !== "function") return () => {};

  const run = () => {
    try {
      // Deliberately not awaited. The page is going; there is nobody left to
      // handle a rejection, and blocking the unload is worse than losing a
      // batch. `keepalive` is what actually carries the request out.
      const r = flush();
      if (r && typeof r.catch === "function") r.catch(() => {});
    } catch {
      // A telemetry flush must never throw during unload — that surfaces as an
      // error in the publisher's page at the worst possible moment.
    }
  };

  const onVisibility = () => {
    // Only on the way OUT. Firing on 'visible' would flush an empty queue every
    // time the visitor switches back to the tab.
    if (target?.visibilityState === "hidden") run();
  };

  target?.addEventListener?.("visibilitychange", onVisibility);
  win?.addEventListener?.("pagehide", run);

  return () => {
    target?.removeEventListener?.("visibilitychange", onVisibility);
    win?.removeEventListener?.("pagehide", run);
  };
}
